import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useTaskStore } from '../stores/taskStore';
import api from '../services/api';
import TopBar from '../components/layout/TopBar';
import Sidebar from '../components/layout/Sidebar';
import MainSidebarContent from '../components/layout/MainSidebarContent';
import ConnectorsAnnouncement from '../components/layout/ConnectorsAnnouncement';
import Plotly from 'plotly.js-basic-dist-min';
import createPlotlyComponent from 'react-plotly.js/factory';
import { plotSafeText, plotSafeValues } from '../utils/plotlyText';

const Plot = createPlotlyComponent(Plotly);

interface Message {
  id: string;
  role: 'user' | 'agent' | 'error';
  content: string;
  timestamp: Date;
  feedbackMeta?: {
    turnId?: string;  // chat_logs.turn_id — feedback is keyed on it (absent for pre-turn_id chats)
    question: string;
    dataset?: string;
    rating?: 'up' | 'down';
    comment?: string;
  };
}

interface ProgressState {
  visible: boolean;
  mode: 'status' | 'thinking';
  text: string;
  thinkingLines: string[];
  thinkingShownAt: number;
}

interface HistorySession {
  session_id: string;
  title?: string;
  last_turn_at?: string;
  turn_count?: number;
  dataset_hint?: string | null;
}

// Chart renderer component
function ChartRenderer({ data }: { data: any }) {
  // Safety check for data
  if (!data || !data.series || !Array.isArray(data.series)) {
    return null;
  }
  
  const isDark = localStorage.getItem('fm-theme') === 'dark';
  
  // Convert custom chart format to Plotly format
  // Model-written text: escape so Plotly can't render injected <a href> links.
  const x = plotSafeValues(data.x);
  const traces = data.series.map((s: any) => ({
    x,
    y: s.data,
    name: plotSafeText(s.name),
    type: data.type === 'area' ? 'scatter' : data.type,
    fill: data.type === 'area' ? 'tonexty' : undefined,
    mode: data.type === 'area' ? 'lines' : undefined,
    stackgroup: data.type === 'area' ? 'one' : undefined,
    line: data.type === 'area' ? { width: 2 } : undefined,
  }));

  // Format y-axis based on format hint
  let yaxis: any = { 
    title: { text: plotSafeText(data.y_label), standoff: 10 },
    tickfont: { size: 12 },
    automargin: true,
  };
  if (data.y_format === 'currency0') {
    yaxis.tickprefix = '$';
    yaxis.tickformat = ',';
    yaxis.separatethousands = true;
  } else if (data.y_format === 'percent') {
    yaxis.ticksuffix = '%';
  }

  return (
    <div style={{
      background: 'var(--surface2)',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid var(--border)'
    }}>
      <Plot
        data={traces}
        layout={{
          title: { 
            text: plotSafeText(data.title),
            font: { size: 16, weight: 600 },
            x: 0.05,
            xanchor: 'left',
          },
          xaxis: { 
            title: { text: plotSafeText(data.x_label), standoff: 10 },
            tickangle: -45,
            tickfont: { size: 11 },
            automargin: true,
          },
          yaxis,
          autosize: true,
          paper_bgcolor: isDark ? '#1a1a1a' : '#ffffff',
          plot_bgcolor: isDark ? '#1a1a1a' : '#ffffff',
          font: { 
            color: isDark ? '#e5e5e5' : '#1a1a1a',
            family: 'system-ui, -apple-system, sans-serif',
          },
          margin: { t: 60, r: 30, b: 80, l: 80 },
          legend: { 
            orientation: 'h', 
            y: -0.25,
            x: 0.5,
            xanchor: 'center',
            font: { size: 12 },
          },
          hovermode: 'x unified',
          showlegend: true,
        }}
        style={{ width: '100%', height: '450px' }}
        config={{ 
          responsive: true, 
          displayModeBar: false,
          displaylogo: false,
        }}
      />
    </div>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { tasks, selectedTask, setTasks, setSelectedTask } = useTaskStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [input, setInput] = useState('');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatsExpanded, setChatsExpanded] = useState(true);
  const [showHistoryPage, setShowHistoryPage] = useState(false);
  const [showDatasetMenu, setShowDatasetMenu] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState<Set<string>>(new Set());
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [openMenuChatId, setOpenMenuChatId] = useState<string | null>(null);
  const [openMenuChatIdHistory, setOpenMenuChatIdHistory] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{top?: number, bottom?: number, right: number} | null>(null);
  const [menuPositionHistory, setMenuPositionHistory] = useState<{top?: number, bottom?: number, right: number} | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(() => {
    // Check both location state and sessionStorage
    return !!location.state?.sessionId || !!sessionStorage.getItem('loadingSession');
  });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [pinnedSessions, setPinnedSessions] = useState<Set<string>>(new Set());
  const [projects, setProjects] = useState<Array<{project_id: string, project_name: string}>>([]);
  const [showRenameModal, setShowRenameModal] = useState<string | null>(null);
  const [showAddToProjectModal, setShowAddToProjectModal] = useState<string | null>(null);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showRenameProjectModal, setShowRenameProjectModal] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [renameProjectName, setRenameProjectName] = useState('');
  const [renameTitle, setRenameTitle] = useState('');
  const [chatProjects, setChatProjects] = useState<Set<string>>(new Set());
  const [openProjectMenuId, setOpenProjectMenuId] = useState<string | null>(null);
  const [historySessions, setHistorySessions] = useState<HistorySession[]>([]);
  const [historyFilter, setHistoryFilter] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [typedGreeting, setTypedGreeting] = useState('');
  const [progress, setProgress] = useState<ProgressState>({
    visible: false,
    mode: 'status',
    text: 'Working on your question…',
    thinkingLines: [],
    thinkingShownAt: 0
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const datasetMenuRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const baseTextRef = useRef('');
  const lastUserMsgRef = useRef('');

  useEffect(() => {
    loadTasks();
    loadTheme();
    loadHistory();
    loadPinnedSessions();
    loadProjects();
    initSpeechRecognition();
    const savedSessionId = localStorage.getItem('fm_session_id');
    if (savedSessionId) setSessionId(savedSessionId);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, progress]);

  // Typing effect for greeting
  useEffect(() => {
    const fullText = `Hello, ${user?.first_name || 'there'}`;
    let currentIndex = 0;
    setTypedGreeting('');
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedGreeting(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 50); // 50ms per character

    return () => clearInterval(typingInterval);
  }, [user?.first_name]);

  // Handle ?agent= query parameter from Agent Store
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const agentParam = params.get('agent');
    if (agentParam && tasks.length > 0) {
      const match = tasks.find(t => t.task_name === agentParam);
      if (match) {
        setSelectedTask(match.task_name);
        // Clear the query param
        navigate('/', { replace: true });
      }
    }
  }, [location.search, tasks, setSelectedTask, navigate]);

  // Handle sessionId from navigation state (from Projects/History)
  useEffect(() => {
    const sessionFromState = location.state?.sessionId;
    const sessionFromStorage = sessionStorage.getItem('loadingSession');
    const sessionToLoad = sessionFromState || sessionFromStorage;
    
    if (sessionToLoad) {
      setIsLoadingSession(true);
      
      // Clear both sources immediately to prevent re-triggering
      if (sessionFromState) {
        navigate('/', { replace: true });
      }
      if (sessionFromStorage) {
        sessionStorage.removeItem('loadingSession');
      }
      setShowHistoryPage(false);
      
      // Then load the session
      loadSession(sessionToLoad)
        .catch((err) => {
          console.error('Failed to load session:', err);
          setIsLoadingSession(false); // Clear on error
        })
        .finally(() => {
          // Small delay to ensure messages are rendered
          setTimeout(() => {
            setIsLoadingSession(false);
          }, 100);
        });
    }
  }, [location.state?.sessionId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (datasetMenuRef.current && !datasetMenuRef.current.contains(target)) {
        setShowDatasetMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking inside a dropdown menu or the ... button
      if (target.closest('[data-dropdown-menu]') || target.closest('[data-menu-button]')) {
        return;
      }
      
      if (openMenuChatId) {
        setOpenMenuChatId(null);
      }
      if (openProjectMenuId) {
        setOpenProjectMenuId(null);
      }
      if (openMenuChatIdHistory) {
        setOpenMenuChatIdHistory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuChatId, openProjectMenuId, openMenuChatIdHistory]);

  const initSpeechRecognition = () => {
    // @ts-ignore
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      baseTextRef.current = input.trimEnd();
      if (baseTextRef.current) baseTextRef.current += ' ';
    };

    recognition.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += transcript;
        else interim += transcript;
      }
      setInput(baseTextRef.current + (final || interim));
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  };

  // Helper function to get relative time string
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  };

  // Delete chats (individual or bulk)
  const deleteChats = async (sessionIds: string[]) => {
    if (sessionIds.length === 0) return;
    
    const confirmMsg = sessionIds.length === 1 
      ? 'Delete this chat? This will permanently remove it from the database.'
      : `Delete ${sessionIds.length} chats? This will permanently remove them from the database.`;
    
    if (!confirm(confirmMsg)) return;
    
    setIsDeleting(true);
    try {
      // Delete each session via API
      for (const sid of sessionIds) {
        await api.delete(`/chat/sessions/${sid}`);
      }
      
      // Refresh history
      await loadHistory();
      
      // Clear selection
      setSelectedChats(new Set());
      
      // If current session was deleted, start new
      if (sessionIds.includes(sessionId)) {
        newAnalysis();
      }
    } catch (err) {
      console.error('Failed to delete chats:', err);
      alert('Failed to delete chats. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle chat selection
  const toggleChatSelection = (sessionId: string) => {
    const newSelection = new Set(selectedChats);
    if (newSelection.has(sessionId)) {
      newSelection.delete(sessionId);
    } else {
      newSelection.add(sessionId);
    }
    setSelectedChats(newSelection);
  };

  // Select/deselect all (current page only)
  const toggleSelectAll = () => {
    const currentPageIds = new Set(paginatedChats.map(s => s.session_id));
    const hasAllCurrentPageSelected = paginatedChats.every(chat => selectedChats.has(chat.session_id));
    
    if (hasAllCurrentPageSelected) {
      // Deselect all on current page
      const newSelection = new Set(selectedChats);
      currentPageIds.forEach(id => newSelection.delete(id));
      setSelectedChats(newSelection);
    } else {
      // Select all on current page
      const newSelection = new Set(selectedChats);
      currentPageIds.forEach(id => newSelection.add(id));
      setSelectedChats(newSelection);
    }
  };

  // Exit select mode
  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedChats(new Set());
    setHistoryPage(1);
  };

  const handleMenuOpen = (sessionId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuHeight = 200; // Approximate menu height
    const openUpward = spaceBelow < menuHeight;
    
    // Calculate fixed position relative to viewport
    if (openUpward) {
      setMenuPosition({
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right
      });
    } else {
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
    
    setOpenMenuChatId(openMenuChatId === sessionId ? null : sessionId);
  };

  const loadTasks = async () => {
    try {
      const res = await api.get('/tasks');
      // Backend returns a bare list; tolerate {tasks: [...]} too.
      const list = Array.isArray(res.data) ? res.data : (res.data?.tasks || []);
      setTasks(list);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get('/history');
      setHistorySessions(res.data.sessions || []);
      setHistoryError('');
    } catch (err) {
      console.error('Failed to load history:', err);
      setHistoryError('Could not load history.');
      setHistorySessions([]);
    }
  };

  const loadPinnedSessions = async () => {
    try {
      const res = await api.get('/chat/pins');
      setPinnedSessions(new Set(res.data.pinned_sessions || []));
    } catch (err) {
      console.error('Failed to load pinned sessions:', err);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await api.get('/api/projects');
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadChatProjects = async (sessionId: string) => {
    try {
      const res = await api.get(`/chat/sessions/${sessionId}/projects`);
      setChatProjects(new Set((res.data.projects || []).map((p: any) => p.project_id)));
    } catch (err) {
      console.error('Failed to load chat projects:', err);
    }
  };

  const pinChat = async (sessionId: string) => {
    try {
      await api.post(`/chat/sessions/${sessionId}/pin`);
      setPinnedSessions(prev => new Set([...prev, sessionId]));
      // Update the session title in historySessions if needed
      const session = historySessions.find(s => s.session_id === sessionId);
      if (session && !session.title) {
        // Load title for this session if it doesn't have one
        setHistorySessions(prev => prev.map(s => 
          s.session_id === sessionId ? { ...s, title: s.title || '(no title)' } : s
        ));
      }
    } catch (err) {
      console.error('Failed to pin chat:', err);
      alert('Failed to pin chat. Please try again.');
    }
  };

  const unpinChat = async (sessionId: string) => {
    try {
      await api.delete(`/chat/sessions/${sessionId}/pin`);
      setPinnedSessions(prev => {
        const updated = new Set(prev);
        updated.delete(sessionId);
        return updated;
      });
    } catch (err) {
      console.error('Failed to unpin chat:', err);
      alert('Failed to unpin chat. Please try again.');
    }
  };

  const renameChat = async (sessionId: string, newTitle: string) => {
    try {
      await api.patch(`/chat/sessions/${sessionId}/title`, { title: newTitle });
      // Update local state immediately
      setHistorySessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, title: newTitle } : s
      ));
    } catch (err) {
      console.error('Failed to rename chat:', err);
      alert('Failed to rename chat. Please try again.');
    }
  };

  const createProject = async (projectName: string) => {
    try {
      const res = await api.post('/api/projects', { project_name: projectName });
      await loadProjects();
      return res.data.project_id;
    } catch (err: any) {
      console.error('Failed to create project:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert(`Failed to create project: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
      return null;
    }
  };

  const renameProject = async (projectId: string, newName: string) => {
    try {
      await api.patch(`/api/projects/${projectId}`, { project_name: newName });
      loadProjects();
    } catch (err) {
      console.error('Failed to rename project:', err);
    }
  };

  const addChatToProject = async (projectId: string, sessionId: string) => {
    try {
      await api.post(`/api/projects/${projectId}/chats/${sessionId}`);
    } catch (err) {
      console.error('Failed to add chat to project:', err);
    }
  };

  const removeChatFromProject = async (projectId: string, sessionId: string) => {
    try {
      await api.delete(`/api/projects/${projectId}/chats/${sessionId}`);
    } catch (err) {
      console.error('Failed to remove chat from project:', err);
    }
  };

  const syncDatasetFromHint = (hint?: string | null) => {
    if (!hint) {
      setSelectedTask(null);
      return;
    }
    const h = String(hint).toLowerCase();
    const match = tasks.find(
      (t) => t.task_name.toLowerCase() === h || (t.bq_dataset || '').toLowerCase() === h
    );
    setSelectedTask(match ? match.task_name : null);
  };

  const loadSession = async (sid: string, datasetHint?: string | null) => {
    try {
      const res = await api.get(`/history/${encodeURIComponent(sid)}`);
      const turns = res.data.turns || [];
      if (!turns.length) return;

      syncDatasetFromHint(datasetHint);
      setSessionId(sid);
      localStorage.setItem('fm_session_id', sid);
      setProgress({ visible: false, mode: 'status', text: '', thinkingLines: [], thinkingShownAt: 0 });

      const restored: Message[] = [];
      for (const t of turns) {
        if (t.question) {
          restored.push({
            id: `${sid}-q-${restored.length}`,
            role: 'user',
            content: t.question,
            timestamp: new Date(t.created_at || Date.now())
          });
        }
        if (t.response) {
          restored.push({
            id: `${sid}-a-${restored.length}`,
            role: 'agent',
            content: t.response,
            timestamp: new Date(t.created_at || Date.now()),
            feedbackMeta: {
              turnId: t.turn_id || undefined,
              question: t.question || '',
              dataset: datasetHint || undefined,
              rating: t.feedback_rating || undefined,
              comment: t.feedback_comment || undefined
            }
          });
        }
      }
      setMessages(restored);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  };

  const historyBucket = (ts?: string) => {
    if (!ts) return 'Older';
    const d = new Date(ts);
    const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays >= 2 && diffDays <= 7) return 'Last 7 days';
    return 'Older';
  };

  const filteredHistory = historySessions.filter((s) => {
    if (!historyFilter.trim()) return true;
    const q = historyFilter.trim().toLowerCase();
    return (s.title || '').toLowerCase().includes(q);
  });

  const groupedHistory = (['Today', 'Yesterday', 'Last 7 days', 'Older'] as const)
    .map((label) => ({
      label,
      items: filteredHistory.filter((s) => historyBucket(s.last_turn_at) === label)
    }))
    .filter((g) => g.items.length > 0);

  // Pagination calculations with useMemo
  const { allChats, totalPages, startIdx, endIdx, paginatedChats } = useMemo(() => {
    const all = groupedHistory.flatMap(g => g.items);
    const pages = Math.ceil(all.length / historyPageSize);
    const start = (historyPage - 1) * historyPageSize;
    const end = start + historyPageSize;
    const paginated = all.slice(start, end);
    
    return {
      allChats: all,
      totalPages: pages,
      startIdx: start,
      endIdx: end,
      paginatedChats: paginated
    };
  }, [groupedHistory, historyPage, historyPageSize]);

  // Reset to valid page if current page is empty
  useEffect(() => {
    if (paginatedChats.length === 0 && allChats.length > 0 && historyPage > 1) {
      setHistoryPage(1);
    }
  }, [paginatedChats.length, allChats.length, historyPage]);

  const loadTheme = () => {
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute('data-theme', saved);
    }
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fm-theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };


  const handleMicrophone = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
      } catch (err) {
        console.error('Failed to start recognition:', err);
      }
    }
  };

  const formatStatus = (evtType: string, payload: any): string => {
    const tool = payload.tool || '';

    if (evtType === 'routing') {
      const labels: Record<string, string> = { bq: 'BigQuery', gcp: 'Research' };
      const label = labels[payload.agent] || payload.agent;
      return `Routing to ${label}…`;
    }

    if (evtType === 'research_start') return payload.message || 'Searching…';
    if (evtType === 'research_step') return payload.message || 'Processing…';
    if (evtType === 'research_done') return '';

    if (evtType === 'tool_start') {
      if (tool === 'list_bq_tables') return `Checking schema for ${payload.dataset_id || 'dataset'}…`;
      if (tool === 'run_bq_query') return 'Running query…';
      return `Calling ${tool}…`;
    }

    if (evtType === 'tool_cost_estimate') {
      const mb = ((payload.estimated_bytes || 0) / 1024 ** 2).toFixed(1);
      return `Query ready (~${mb} MB).`;
    }

    if (evtType === 'tool_end') {
      if (!payload.ok) {
        const err = (payload.error || '').toLowerCase();
        if (err.includes('bytes billed') || err.includes('cost'))
          return 'Cost limit reached — retrying with narrower date range…';
        if (err.includes('not found') || err.includes('column') || err.includes('schema'))
          return 'Column not found — checking schema…';
        if (err.includes('access denied') || err.includes('permission'))
          return 'Access denied — checking permissions…';
        return 'Query failed — retrying…';
      }
      if (tool === 'list_bq_tables') {
        const n = payload.tables_count ?? '?';
        return `Schema loaded (${n} table${n === 1 ? '' : 's'}).`;
      }
      if (tool === 'run_bq_query') {
        const n = payload.row_count ?? '?';
        const cached = payload.cache_hit ? ' · from cache' : '';
        const mb = (!payload.cache_hit && payload.bytes_processed != null)
          ? ` · ${(payload.bytes_processed / 1024 ** 2).toFixed(1)} MB` : '';
        return n === 0 || n === '0'
          ? `Query returned no rows${mb}${cached}.`
          : `Query complete — ${n} row${n === 1 ? '' : 's'} returned${mb}${cached}.`;
      }
      return 'Done.';
    }

    return '';
  };

  const newAnalysis = () => {
    setMessages([]);
    setSessionId('');
    localStorage.removeItem('fm_session_id');
    setProgress({ visible: false, mode: 'status', text: '', thinkingLines: [], thinkingShownAt: 0 });
    setSelectedTask(null);
    loadHistory();
  };

  /** Logo home: reset to welcome chat on `/`. */
  const goHome = () => {
    newAnalysis();
    if (window.location.pathname !== '/') navigate('/');
  };

  const sendMessage = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || isLoading) return;
    if (text.length > 5000) {
      addErrorMessage(`Message too long (${text.length} / 5000 chars).`);
      return;
    }

    lastUserMsgRef.current = text;
    const dataset = selectedTask || null;

    setInput('');
    setIsLoading(true);

    // Add user message
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Show progress bubble
    setProgress({
      visible: true,
      mode: 'status',
      text: 'Working on your question…',
      thinkingLines: [],
      thinkingShownAt: 0
    });

    try {
      // Generate session ID for new conversations
      const currentSessionId = sessionId || crypto.randomUUID();
      if (!sessionId) {
        setSessionId(currentSessionId);
      }
      
      const body: any = { message: text, session_id: currentSessionId };
      if (dataset) body.dataset_hint = dataset;

      const response = await fetch('/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replace(/\r/g, '');

        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          const trimmed = chunk.trim();
          if (!trimmed) continue;

          let evtType = 'message';
          let dataStr = '';
          const lines = trimmed.split('\n');
          for (const ln of lines) {
            if (ln.startsWith('event:')) evtType = ln.slice(6).trim();
            else if (ln.startsWith('data:')) dataStr += ln.slice(5).trim();
          }

          let payload: any = null;
          try {
            payload = dataStr ? JSON.parse(dataStr) : {};
          } catch {
            payload = { raw: dataStr };
          }

          if (evtType === 'thinking') {
            const snippet = (payload.text || '').slice(0, 240);
            setProgress(prev => {
              const newLines = [...prev.thinkingLines];
              if (newLines[newLines.length - 1] !== snippet) {
                newLines.push(snippet);
                if (newLines.length > 3) newLines.shift();
              }
              return {
                visible: true,
                mode: 'thinking',
                text: '',
                thinkingLines: newLines,
                thinkingShownAt: Date.now()
              };
            });
          } else if (evtType === 'status') {
            setProgress(prev => ({
              ...prev,
              visible: true,
              mode: 'status',
              text: payload.text || '',
              thinkingLines: []
            }));
          } else if (['routing', 'tool_start', 'tool_cost_estimate', 'tool_end',
                       'research_start', 'research_step', 'research_done'].includes(evtType)) {
            const status = formatStatus(evtType, payload);
            if (status) {
              setProgress(prev => {
                if (prev.mode === 'thinking') {
                  const elapsed = Date.now() - prev.thinkingShownAt;
                  const wait = Math.max(0, 5000 - elapsed);
                  if (wait > 0) {
                    setTimeout(() => {
                      setProgress(p => ({ ...p, mode: 'status', text: status, thinkingLines: [] }));
                    }, wait);
                    return prev;
                  }
                }
                return { ...prev, visible: true, mode: 'status', text: status, thinkingLines: [] };
              });
            }
          } else if (evtType === 'final') {
            const newSessionId = payload.session_id;
            if (newSessionId) {
              setSessionId(newSessionId);
              localStorage.setItem('fm_session_id', newSessionId);
            }

            const agentMsg: Message = {
              id: Date.now().toString(),
              role: 'agent',
              content: payload.response || '',
              timestamp: new Date(),
              feedbackMeta: { turnId: payload.turn_id || undefined, question: text, dataset: dataset || undefined }
            };

            setMessages(prev => [...prev, agentMsg]);
            setProgress({ visible: false, mode: 'status', text: '', thinkingLines: [], thinkingShownAt: 0 });
            setIsLoading(false);
            loadHistory();
            return;
          } else if (evtType === 'error') {
            setProgress({ visible: false, mode: 'status', text: '', thinkingLines: [], thinkingShownAt: 0 });
            addErrorMessage(`Error: ${payload.error || 'Unknown error'}`);
            setIsLoading(false);
            return;
          }
        }
      }

      throw new Error('Stream ended unexpectedly');
    } catch (err: any) {
      setProgress({ visible: false, mode: 'status', text: '', thinkingLines: [], thinkingShownAt: 0 });
      addErrorMessage(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addErrorMessage = (text: string) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'error',
      content: text,
      timestamp: new Date()
    }]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessage(text);
  };

  // Returns false when the save failed so the bubble can show an inline error.
  const sendFeedback = async (msgId: string, rating: 'up' | 'down', comment?: string): Promise<boolean> => {
    const msg = messages.find(m => m.id === msgId);
    if (!msg || !msg.feedbackMeta) return false;

    try {
      await api.post('/feedback', {
        session_id: sessionId,
        turn_id: msg.feedbackMeta.turnId || null,
        rating,
        question: msg.feedbackMeta.question,
        response: msg.content,
        dataset_hint: msg.feedbackMeta.dataset || null,
        comment: comment || null
      });

      setMessages(prev => prev.map(m =>
        m.id === msgId ? { ...m, feedbackMeta: { ...m.feedbackMeta!, rating, comment: comment || undefined } } : m
      ));
      return true;
    } catch (err) {
      console.error('Failed to send feedback:', err);
      return false;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  };

  const composer = (
    <div style={{
      width: '100%',
      maxWidth: '720px',
      margin: '0 auto',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: '16px',
      padding: '14px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
    }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Pearson about your performance data..."
          disabled={isLoading}
          rows={1}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text)',
            fontSize: '14px',
            resize: 'none',
            minHeight: '28px',
            maxHeight: '140px',
            lineHeight: '1.5',
            fontFamily: 'inherit'
          }}
        />
        
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {/* Mic */}
          <button
            onClick={handleMicrophone}
            title={isListening ? 'Stop listening' : 'Voice input'}
            style={{
              width: '32px', height: '32px', borderRadius: '50%',
              border: 'none',
              background: isListening ? 'var(--red)' : 'transparent',
              color: isListening ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .15s',
              animation: isListening ? 'pulse 1.5s ease-in-out infinite' : 'none',
              flexShrink: 0
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '15px', height: '15px' }}>
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
            </svg>
          </button>
          {/* Send */}
          <button
            onClick={() => sendMessage()}
            disabled={isLoading || !input.trim()}
            title="Send"
            style={{
              width: '34px', height: '34px', borderRadius: '50%',
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              opacity: isLoading || !input.trim() ? 0.45 : 1,
              transition: 'opacity .15s',
              flexShrink: 0
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ width: '16px', height: '16px' }}>
              <line x1="12" y1="19" x2="12" y2="5"/>
              <polyline points="5 12 12 5 19 12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <ConnectorsAnnouncement email={user?.email} />
      <Sidebar
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onHomeClick={() => {
          newAnalysis();
          setShowHistoryPage(false);
          setIsSelectMode(false);
          setSelectedChats(new Set());
        }}
      >

        <MainSidebarContent
          currentPage="chat"
          onNewChat={newAnalysis}
          onShowHistory={() => setShowHistoryPage(true)}
        />

        {/* Search for chat history */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginTop: '12px' }}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" style={{
            position: 'absolute', left: '10px', width: '16px', height: '16px', color: 'var(--text-muted)', pointerEvents: 'none'
          }}>
            <circle cx="9" cy="9" r="7"/><line x1="14" y1="14" x2="18" y2="18"/>
          </svg>
          <input
            type="text"
            value={historyFilter}
            onChange={(e) => setHistoryFilter(e.target.value)}
            placeholder="Search chats"
            style={{
              width: '100%',
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              fontSize: '13px',
              outline: 'none',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {/* Pinned Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '12px' }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-muted)',
            padding: '0 0 2px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '11px', height: '11px', flexShrink: 0 }}>
              <line x1="12" y1="17" x2="12" y2="22"/>
              <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
            </svg>
            <span>Pinned</span>
          </div>
          {pinnedSessions.size === 0 ? null : (
            Array.from(pinnedSessions).map((sid) => {
              const session = historySessions.find(s => s.session_id === sid);
              if (!session) return null;
              
              const active = sid === sessionId;
              
              return (
                <div
                  key={sid}
                  onClick={() => {
                    setIsLoadingSession(true);
                    loadSession(sid, session.dataset_hint)
                      .catch((err) => console.error('Failed to load session:', err))
                      .finally(() => setTimeout(() => setIsLoadingSession(false), 100));
                  }}
                  onMouseEnter={() => setHoveredChatId(sid)}
                  onMouseLeave={() => setHoveredChatId(null)}
                  style={{
                    cursor: 'pointer',
                    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                    background: active ? 'var(--surface)' : 'var(--surface2)',
                    borderRadius: '8px',
                    padding: '8px',
                    marginBottom: '6px',
                    transition: 'border-color 0.15s, background 0.15s',
                    position: 'relative',
                    zIndex: openMenuChatId === sid ? 9999 : 'auto'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'var(--accent)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = active ? 'var(--accent)' : 'var(--border)';
                  }}
                >
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text)',
                    lineHeight: 1.3,
                    marginBottom: '6px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    paddingRight: hoveredChatId === sid ? '24px' : '0'
                  }}>
                    {session.title || '(no title)'}
                  </div>
                  {hoveredChatId === sid && (
          <button
                      data-menu-button="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(sid, e);
                      }}
            style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  )}
                  {openMenuChatId === sid && menuPosition && (
                    <div
                      data-dropdown-menu="true"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'fixed',
                        ...(menuPosition.top !== undefined ? {
                          top: `${menuPosition.top}px`
                        } : {
                          bottom: `${menuPosition.bottom}px`
                        }),
                        right: `${menuPosition.right}px`,
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        minWidth: '180px',
                        zIndex: 10000,
                        overflow: 'hidden'
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          unpinChat(sid);
                          setOpenMenuChatId(null);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
                          fontSize: '14px',
                          color: 'var(--text)',
              fontFamily: 'inherit',
                          transition: 'background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--surface2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                          <line x1="12" y1="17" x2="12" y2="22"/>
                          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
            </svg>
                        Unpin
          </button>
          <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuChatId(null);
                          setShowRenameModal(sid);
                          setRenameTitle(session.title || '');
                        }}
            style={{
              width: '100%',
                          padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: 'var(--text)',
              fontFamily: 'inherit',
                          transition: 'background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--surface2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
                        Rename
          </button>
          <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuChatId(null);
                          setShowAddToProjectModal(sid);
                          loadChatProjects(sid);
                        }}
            style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: 'var(--text)',
                          fontFamily: 'inherit',
                          transition: 'background 0.15s',
              display: 'flex',
              alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'var(--surface2)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                          <line x1="12" y1="11" x2="12" y2="17"/>
                          <line x1="9" y1="14" x2="15" y2="14"/>
                        </svg>
                        Add to project
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuChatId(null);
                          if (confirm('Delete this chat? This cannot be undone.')) {
                            deleteChats([sid]);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
              background: 'transparent',
              border: 'none',
                          textAlign: 'left',
              cursor: 'pointer',
                          fontSize: '14px',
                          color: 'var(--error, #dc2626)',
              fontFamily: 'inherit',
                          transition: 'background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--surface2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 0 4px 0',
              marginBottom: '8px'
            }}
          >
            {/* Chats label + toggle arrow */}
            <button
              type="button"
              onClick={() => setChatsExpanded(!chatsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit'
            }}
          >
            <span style={{
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-muted)'
            }}>
              Chats
            </span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
              width: '13px',
              height: '13px',
              color: 'var(--text-muted)',
              transform: chatsExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s'
            }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          </div>
          {chatsExpanded && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              paddingRight: '4px'
            }}>
            {historyError ? (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{historyError}</div>
            ) : groupedHistory.length === 0 ? (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No recent chats.</div>
            ) : (
              groupedHistory.map((group) => (
                <div key={group.label} style={{ marginTop: '2px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '4px 0' }}>
                    {group.label}
                  </div>
                  {group.items.map((s) => {
                    const active = s.session_id === sessionId;
                    const dt = s.last_turn_at ? new Date(s.last_turn_at) : null;
                    const isPinned = pinnedSessions.has(s.session_id);
                    
                    return (
                      <div
                        key={s.session_id}
                        onClick={() => {
                          setIsLoadingSession(true);
                          loadSession(s.session_id, s.dataset_hint)
                            .catch((err) => console.error('Failed to load session:', err))
                            .finally(() => setTimeout(() => setIsLoadingSession(false), 100));
                        }}
                        onMouseEnter={() => setHoveredChatId(s.session_id)}
                        onMouseLeave={() => setHoveredChatId(null)}
                        style={{
                          cursor: 'pointer',
                          border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                          background: active ? 'var(--surface)' : 'var(--surface2)',
                          borderRadius: '8px',
                          padding: '8px',
                          marginBottom: '6px',
                          transition: 'border-color 0.15s, background 0.15s',
                          position: 'relative',
                          zIndex: openMenuChatId === s.session_id ? 9999 : 'auto'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = 'var(--accent)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = active ? 'var(--accent)' : 'var(--border)';
                        }}
                      >
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text)',
                          lineHeight: 1.3,
                          marginBottom: '6px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          paddingRight: hoveredChatId === s.session_id ? '24px' : '0'
                        }}>
                          {s.title || '(no title)'}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {dt ? dt.toLocaleDateString() : ''}
                        </div>
                        {hoveredChatId === s.session_id && (
                          <button
                            data-menu-button="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(s.session_id, e);
                            }}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              color: 'var(--text-muted)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                              <circle cx="12" cy="5" r="2"/>
                              <circle cx="12" cy="12" r="2"/>
                              <circle cx="12" cy="19" r="2"/>
                            </svg>
                          </button>
                        )}
                        {openMenuChatId === s.session_id && menuPosition && (
                          <div
                            data-dropdown-menu="true"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'fixed',
                              ...(menuPosition.top !== undefined ? {
                                top: `${menuPosition.top}px`
                              } : {
                                bottom: `${menuPosition.bottom}px`
                              }),
                              right: `${menuPosition.right}px`,
                              background: 'var(--surface)',
                              border: '1px solid var(--border)',
                              borderRadius: '8px',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                              minWidth: '180px',
                              zIndex: 10000,
                              overflow: 'hidden'
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPinned) {
                                  unpinChat(s.session_id);
                                } else {
                                  pinChat(s.session_id);
                                }
                                setOpenMenuChatId(null);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'var(--text)',
                                fontFamily: 'inherit',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'var(--surface2)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                <line x1="12" y1="17" x2="12" y2="22"/>
                                <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
                              </svg>
                              {isPinned ? 'Unpin' : 'Pin'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuChatId(null);
                                setShowRenameModal(s.session_id);
                                setRenameTitle(s.title || '');
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'var(--text)',
                                fontFamily: 'inherit',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'var(--surface2)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                              Rename
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuChatId(null);
                                setShowAddToProjectModal(s.session_id);
                                loadChatProjects(s.session_id);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'var(--text)',
                                fontFamily: 'inherit',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'var(--surface2)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                <line x1="12" y1="11" x2="12" y2="17"/>
                                <line x1="9" y1="14" x2="15" y2="14"/>
                              </svg>
                              Add to project
                            </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuChatIdHistory(null);
                                    if (confirm('Delete this chat? This cannot be undone.')) {
                                      deleteChats([s.session_id]);
                                    }
                                  }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'var(--error, #dc2626)',
                                fontFamily: 'inherit',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'var(--surface2)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            </div>
          )}
        </div>

      </Sidebar>

      {/* Main area - either chat or history page */}
      {showHistoryPage ? (
        /* Full History Page */
        <main style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          background: 'var(--bg)'
        }}>
          <TopBar
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(true)}
            theme={theme}
            onToggleTheme={toggleTheme}
            user={user}
            onHomeClick={() => {
              newAnalysis();
              setShowHistoryPage(false);
              setIsSelectMode(false);
              setSelectedChats(new Set());
            }}
          />

          {/* History Page Content - Centered with max-width */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            justifyContent: 'center',
            padding: '40px 24px'
          }}>
            <div style={{ width: '100%', maxWidth: '900px' }}>
              {/* Header with title and actions */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '32px',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <h1 style={{
                  margin: 0,
                  fontSize: '32px',
                  fontWeight: 600,
                  color: 'var(--text)'
                }}>
                  Chat History
                </h1>

                {/* Selection controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {isSelectMode ? (
                    <>
                      {selectedChats.size > 0 && (
                        <>
                          <span style={{ fontSize: '14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {selectedChats.size} selected
                          </span>
                          <button
                            onClick={() => deleteChats(Array.from(selectedChats))}
                            disabled={isDeleting}
                            style={{
                              background: 'var(--error, #dc2626)',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              cursor: isDeleting ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'white',
                              fontFamily: 'inherit',
                              opacity: isDeleting ? 0.5 : 1,
                              transition: 'opacity 0.15s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </button>
                        </>
                      )}
                      <button
                        onClick={toggleSelectAll}
                        style={{
                          background: 'var(--surface2)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'var(--text)',
                          fontFamily: 'inherit',
                          transition: 'all 0.15s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'var(--surface)';
                          e.currentTarget.style.borderColor = 'var(--accent)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'var(--surface2)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                    >
                      {paginatedChats.every(chat => selectedChats.has(chat.session_id)) && paginatedChats.length > 0
                        ? 'Deselect All' 
                        : 'Select All'}
                    </button>
                      <button
                        onClick={exitSelectMode}
                        style={{
                          background: 'var(--surface2)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'var(--text)',
                          fontFamily: 'inherit',
                          transition: 'all 0.15s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'var(--surface)';
                          e.currentTarget.style.borderColor = 'var(--accent)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'var(--surface2)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                        }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsSelectMode(true)}
                      style={{
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--text)',
                        fontFamily: 'inherit',
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'var(--surface)';
                        e.currentTarget.style.borderColor = 'var(--accent)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'var(--surface2)';
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                    >
                      Select
                    </button>
                  )}
                </div>
              </div>

              {/* Compact list of chats */}
              <div style={{
                background: 'var(--surface)',
                borderRadius: '12px',
                border: '1px solid var(--border)',
                overflow: 'hidden'
              }}>
                {paginatedChats.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: 'var(--text-muted)',
                    fontSize: '15px'
                  }}>
                    {allChats.length === 0 ? 'No chat history yet. Start a conversation to see it here!' : 'No chats on this page.'}
                  </div>
                ) : (
                  paginatedChats.map((s, idx) => {
                    const active = s.session_id === sessionId;
                    const dt = s.last_turn_at ? new Date(s.last_turn_at) : null;
                    const isSelected = selectedChats.has(s.session_id);
                    const isHovered = hoveredChatId === s.session_id;
                    const isLast = idx === paginatedChats.length - 1;
                    
                    return (
                      <div
                        key={s.session_id}
                        onMouseEnter={() => setHoveredChatId(s.session_id)}
                        onMouseLeave={() => setHoveredChatId(null)}
                        style={{
                          borderBottom: isLast ? 'none' : '1px solid var(--border)',
                          background: active ? 'var(--surface2)' : isHovered && !isSelectMode ? 'var(--surface2)' : 'transparent',
                          transition: 'background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '14px 16px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          if (isSelectMode) {
                            toggleChatSelection(s.session_id);
                          } else {
                            // Load session first, then close history page
                            setIsLoadingSession(true);
                            loadSession(s.session_id, s.dataset_hint)
                              .catch((err) => console.error('Failed to load session:', err))
                              .finally(() => {
                                setTimeout(() => {
                                  setIsLoadingSession(false);
                                  setShowHistoryPage(false);
                                }, 100);
                              });
                          }
                        }}
                      >
                        {/* Checkbox (only in select mode) */}
                        {isSelectMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleChatSelection(s.session_id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              flexShrink: 0,
                              accentColor: 'var(--accent)'
                            }}
                          />
                        )}

                        {/* Chat content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: active ? 600 : 400,
                            color: 'var(--text)',
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {s.title || '(no title)'}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: 'var(--text-muted)'
                          }}>
                            {dt ? getRelativeTime(dt) : 'Unknown date'}
                          </div>
                        </div>

                        {/* Three-dot menu (only in normal mode, on hover) */}
                        {!isSelectMode && isHovered && (
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <button
                              data-menu-button="true"
                              onClick={(e) => {
                                e.stopPropagation();
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const menuHeight = 200;
                                
                                if (spaceBelow < menuHeight) {
                                  setMenuPositionHistory({
                                    bottom: window.innerHeight - rect.top + 4,
                                    right: window.innerWidth - rect.right
                                  });
                                } else {
                                  setMenuPositionHistory({
                                    top: rect.bottom + 4,
                                    right: window.innerWidth - rect.right
                                  });
                                }
                                
                                setOpenMenuChatIdHistory(openMenuChatIdHistory === s.session_id ? null : s.session_id);
                              }}
                              disabled={isDeleting}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '4px 8px',
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: isDeleting ? 0.5 : 1,
                                fontSize: '20px',
                                color: 'var(--text-muted)',
                                lineHeight: 1
                              }}
                            >
                              ⋯
                            </button>
                            
                            {/* Dropdown menu */}
                            {openMenuChatIdHistory === s.session_id && menuPositionHistory && (
                              <div
                                data-dropdown-menu="true"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'fixed',
                                  ...(menuPositionHistory.top !== undefined ? {
                                    top: `${menuPositionHistory.top}px`
                                  } : {
                                    bottom: `${menuPositionHistory.bottom}px`
                                  }),
                                  right: `${menuPositionHistory.right}px`,
                                  background: 'var(--surface)',
                                  border: '1px solid var(--border)',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  minWidth: '180px',
                                  zIndex: 10000,
                                  overflow: 'hidden'
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const isPinned = pinnedSessions.has(s.session_id);
                                    if (isPinned) {
                                      unpinChat(s.session_id);
                                    } else {
                                      pinChat(s.session_id);
                                    }
                                    setOpenMenuChatIdHistory(null);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--text)',
                                    fontFamily: 'inherit',
                                    transition: 'background 0.15s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--surface2)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <line x1="12" y1="17" x2="12" y2="22"/>
                                    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
                                  </svg>
                                  {pinnedSessions.has(s.session_id) ? 'Unpin' : 'Pin'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuChatIdHistory(null);
                                    setShowRenameModal(s.session_id);
                                    setRenameTitle(s.title || '');
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--text)',
                                    fontFamily: 'inherit',
                                    transition: 'background 0.15s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--surface2)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                  Rename
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuChatIdHistory(null);
                                    setShowAddToProjectModal(s.session_id);
                                    loadChatProjects(s.session_id);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--text)',
                                    fontFamily: 'inherit',
                                    transition: 'background 0.15s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--surface2)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                    <line x1="12" y1="11" x2="12" y2="17"/>
                                    <line x1="9" y1="14" x2="15" y2="14"/>
                                  </svg>
                                  Add to project
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuChatId(null);
                                    deleteChats([s.session_id]);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--error, #dc2626)',
                                    fontFamily: 'inherit',
                                    transition: 'background 0.15s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--surface2)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  marginTop: '32px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    {startIdx + 1}-{Math.min(endIdx, allChats.length)} of {allChats.length}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                      disabled={historyPage === 1}
                      style={{
                        padding: '8px 12px',
                        background: historyPage === 1 ? 'var(--surface2)' : 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: historyPage === 1 ? 'var(--text-muted)' : 'var(--text)',
                        cursor: historyPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        transition: 'all 0.15s'
                      }}
                      onMouseOver={(e) => {
                        if (historyPage !== 1) {
                          e.currentTarget.style.borderColor = 'var(--accent)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first, last, current, and adjacent pages
                          return page === 1 || 
                                 page === totalPages || 
                                 Math.abs(page - historyPage) <= 1;
                        })
                        .map((page, idx, arr) => {
                          // Add ellipsis between non-consecutive pages
                          const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <span style={{ 
                                  padding: '8px 4px', 
                                  color: 'var(--text-muted)',
                                  fontSize: '14px'
                                }}>
                                  ...
                                </span>
                              )}
                              <button
                                onClick={() => setHistoryPage(page)}
                                style={{
                                  minWidth: '36px',
                                  padding: '8px 12px',
                                  background: page === historyPage ? 'var(--accent)' : 'var(--surface)',
                                  border: `1px solid ${page === historyPage ? 'var(--accent)' : 'var(--border)'}`,
                                  borderRadius: '8px',
                                  color: page === historyPage ? 'white' : 'var(--text)',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: page === historyPage ? 600 : 500,
                                  fontFamily: 'inherit',
                                  transition: 'all 0.15s'
                                }}
                                onMouseOver={(e) => {
                                  if (page !== historyPage) {
                                    e.currentTarget.style.borderColor = 'var(--accent)';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (page !== historyPage) {
                                    e.currentTarget.style.borderColor = 'var(--border)';
                                  }
                                }}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <button
                      onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={historyPage === totalPages}
                      style={{
                        padding: '8px 12px',
                        background: historyPage === totalPages ? 'var(--surface2)' : 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: historyPage === totalPages ? 'var(--text-muted)' : 'var(--text)',
                        cursor: historyPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        transition: 'all 0.15s'
                      }}
                      onMouseOver={(e) => {
                        if (historyPage !== totalPages) {
                          e.currentTarget.style.borderColor = 'var(--accent)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Bottom spacer for breathing room */}
              <div style={{ height: '120px' }} />
            </div>
          </div>
        </main>
      ) : (
        /* Main chat area */
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TopBar
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(true)}
            theme={theme}
            onToggleTheme={toggleTheme}
            user={user}
            onHomeClick={() => {
              newAnalysis();
              setShowHistoryPage(false);
              setIsSelectMode(false);
              setSelectedChats(new Set());
            }}
          />

        {/* Messages area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {messages.length === 0 && !progress.visible && !isLoadingSession ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '24px',
              width: '100%'
            }}>
              <button
                type="button"
                onClick={goHome}
                title="Home"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  marginBottom: '28px',
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 28px auto'
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 30% 30%, rgba(167, 139, 250, 0.7), rgba(139, 92, 246, 0.85), rgba(109, 40, 217, 0.95), rgba(88, 28, 135, 1))',
                  boxShadow: '0 20px 60px rgba(139, 92, 246, 0.7), inset -10px -10px 25px rgba(0, 0, 0, 0.4), inset 10px 10px 25px rgba(255, 255, 255, 0.3)',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  animation: 'float 6s ease-in-out infinite, glow 3s ease-in-out infinite alternate',
                  display: 'block',
                  overflow: 'visible',
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}>
                  {/* Planet surface texture */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 25% 25%, transparent 20%, rgba(109, 40, 217, 0.3) 40%, transparent 60%)',
                    animation: 'surfaceMove 20s linear infinite'
                  }} />
                  
                  {/* Abstract liquid blobs */}
                  <div style={{
                    position: 'absolute',
                    top: '20%',
                    right: '15%',
                    width: '40%',
                    height: '40%',
                    borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                    background: 'rgba(167, 139, 250, 0.3)',
                    filter: 'blur(12px)',
                    animation: 'liquidBlob1 8s ease-in-out infinite'
                  }} />
                  
                  <div style={{
                    position: 'absolute',
                    bottom: '25%',
                    left: '18%',
                    width: '35%',
                    height: '35%',
                    borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
                    background: 'rgba(109, 40, 217, 0.25)',
                    filter: 'blur(10px)',
                    animation: 'liquidBlob2 10s ease-in-out infinite reverse'
                  }} />
                  
                  <div style={{
                    position: 'absolute',
                    top: '35%',
                    left: '10%',
                    width: '30%',
                    height: '30%',
                    borderRadius: '70% 30% 50% 50% / 30% 70% 70% 30%',
                    background: 'rgba(139, 92, 246, 0.2)',
                    filter: 'blur(14px)',
                    animation: 'liquidBlob3 12s ease-in-out infinite'
                  }} />
                  
                  {/* Liquid shine - main highlight */}
                  <div style={{
                    position: 'absolute',
                    top: '8%',
                    left: '12%',
                    width: '55%',
                    height: '55%',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 35% 35%, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.3) 45%, transparent 70%)',
                    filter: 'blur(6px)',
                    animation: 'waterShine 5s ease-in-out infinite'
                  }} />
                  
                  {/* Atmospheric glow layers */}
                  <div style={{
                    position: 'absolute',
                    top: '-10%',
                    left: '-10%',
                    width: '120%',
                    height: '120%',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(167, 139, 250, 0.15), transparent 70%)',
                    filter: 'blur(8px)',
                    animation: 'atmosphereGlow 4s ease-in-out infinite'
                  }} />
                  
                  {/* Liquid wave effect */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                    height: '65%',
                    background: 'linear-gradient(0deg, rgba(109, 40, 217, 0.6), transparent)',
                    borderRadius: '50%',
                    animation: 'wave1 4s ease-in-out infinite',
                    opacity: 0.6
                  }} />
                  
                  {/* Close orbit dust ring */}
                  {[...Array(15)].map((_, i) => {
                    const size = 2 + (i % 3) * 0.5;
                    const delay = i * 0.2;
                    const duration = 8 + (i % 3) * 2;
                    return (
                      <div
                        key={`close-${i}`}
                        style={{
                          position: 'absolute',
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: '50%',
                          background: `rgba(${167 + (i % 2) * 15}, ${139 - (i % 2) * 5}, 250, ${0.6 + (i % 3) * 0.1})`,
                          boxShadow: `0 0 ${4 + (i % 2)}px rgba(167, 139, 250, 1)`,
                          top: '50%',
                          left: '50%',
                          marginLeft: '-1px',
                          marginTop: '-1px',
                          animation: `orbit${i % 4} ${duration}s ease-in-out infinite`,
                          animationDelay: `${delay}s`,
                          opacity: 0.7
                        }}
                      />
                    );
                  })}
                  
                  {/* Mid orbit dust */}
                  {[...Array(20)].map((_, i) => {
                    const size = 1.5 + (i % 4) * 0.5;
                    const delay = i * 0.25;
                    const duration = 10 + (i % 4) * 2;
                    return (
                      <div
                        key={`mid-${i}`}
                        style={{
                          position: 'absolute',
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: '50%',
                          background: `rgba(${147 + (i % 3) * 10}, ${129 - (i % 2) * 8}, 240, ${0.5 + (i % 3) * 0.08})`,
                          boxShadow: `0 0 ${3 + (i % 2)}px rgba(147, 129, 240, 0.9)`,
                          top: '50%',
                          left: '50%',
                          marginLeft: '-1px',
                          marginTop: '-1px',
                          animation: `orbit${(i + 1) % 4} ${duration}s ease-in-out infinite`,
                          animationDelay: `${delay}s`,
                          opacity: 0.6
                        }}
                      />
                    );
                  })}
                  
                  {/* Outer orbit dust field */}
                  {[...Array(25)].map((_, i) => {
                    const size = 1 + (i % 5) * 0.4;
                    const delay = i * 0.3;
                    const duration = 12 + (i % 5) * 2;
                    return (
                      <div
                        key={`outer-${i}`}
                        style={{
                          position: 'absolute',
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: '50%',
                          background: `rgba(${137 + (i % 4) * 8}, ${119 - (i % 3) * 6}, 230, ${0.4 + (i % 4) * 0.06})`,
                          boxShadow: `0 0 ${2 + (i % 2)}px rgba(137, 119, 230, 0.7)`,
                          top: '50%',
                          left: '50%',
                          marginLeft: '-1px',
                          marginTop: '-1px',
                          animation: `orbit${(i + 2) % 4} ${duration}s ease-in-out infinite`,
                          animationDelay: `${delay}s`,
                          opacity: 0.5
                        }}
                      />
                    );
                  })}
                </div>
              </button>
              <h1 style={{
                fontSize: '38px',
                fontWeight: 700,
                background: 'linear-gradient(90deg, #a78bfa, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '8px',
                minHeight: '46px' // Prevent layout shift
              }}>
                {typedGreeting}
                {typedGreeting.length < `Hello, ${user?.first_name || 'there'}`.length && (
                  <span style={{ animation: 'blink 1s infinite' }}>|</span>
                )}
              </h1>
              <h2 style={{ fontSize: '26px', fontWeight: 700, color: 'var(--text)', marginBottom: '36px' }}>
                What would you like to understand?
              </h2>

              <div style={{ width: '100%', maxWidth: '720px' }}>
                {composer}
                {/* Agent selector below input on left side */}
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'var(--text-muted)'
                  }}>
                    Agent:
                  </span>
                  <div ref={datasetMenuRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowDatasetMenu((v) => !v)}
                      aria-haspopup="listbox"
                      aria-expanded={showDatasetMenu}
                      aria-label="Select agent"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'var(--surface2)',
                        border: `1px solid ${showDatasetMenu ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: '8px',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: '11px',
                        fontWeight: 500,
                        transition: 'border-color 0.15s, color 0.15s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.color = 'var(--text)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.color = 'var(--text-muted)';
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '12px', height: '12px', flexShrink: 0 }}>
                        <rect x="5" y="8" width="14" height="10" rx="3"/>
                        <path d="M9 8V7a3 3 0 0 1 6 0v1"/>
                        <circle cx="9.5" cy="13" r="1" fill="currentColor" stroke="none"/>
                        <circle cx="14.5" cy="13" r="1" fill="currentColor" stroke="none"/>
                      </svg>
                      <span>
                        {selectedTask
                          ? (tasks.find((t) => t.task_name === selectedTask)?.display_name || selectedTask)
                          : 'Auto-select'}
                      </span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                        width: '10px',
                        height: '10px',
                        flexShrink: 0,
                        transform: showDatasetMenu ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.15s'
                      }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>

                    {showDatasetMenu && (
                      <div
                        role="listbox"
                        style={{
                          position: 'absolute',
                          left: 0,
                          bottom: 'calc(100% + 6px)',
                          minWidth: '280px',
                          maxHeight: '320px',
                          overflowY: 'auto',
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                          zIndex: 3000,
                          padding: '4px'
                        }}
                      >
                        <button
                          type="button"
                          role="option"
                          aria-selected={!selectedTask}
                          onClick={() => {
                            setSelectedTask(null);
                            setShowDatasetMenu(false);
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            background: !selectedTask ? 'var(--surface2)' : 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'var(--text)',
                            fontSize: '13px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = !selectedTask ? 'var(--surface2)' : 'transparent';
                          }}
                        >
                          <span style={{ flex: 1, fontWeight: !selectedTask ? 600 : 400 }}>Auto-select</span>
                          {!selectedTask && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" style={{ width: '14px', height: '14px' }}>
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                        {tasks.map((task) => {
                          const active = selectedTask === task.task_name;
                          return (
                            <button
                              key={task.task_name}
                              type="button"
                              role="option"
                              aria-selected={active}
                              onClick={() => {
                                setSelectedTask(task.task_name);
                                setShowDatasetMenu(false);
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '10px 12px',
                                background: active ? 'var(--surface2)' : 'transparent',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'var(--text)',
                                fontSize: '13px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'inherit'
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = active ? 'var(--surface2)' : 'transparent';
                              }}
                            >
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontWeight: active ? 600 : 500, lineHeight: 1.3 }}>
                                  {task.display_name || task.task_name}
                                </span>
                                {task.bq_dataset && (
                                  <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    {task.bq_dataset}
                                  </span>
                                )}
                              </span>
                              {active && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '2px' }}>
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </button>
                          );
                        })}
                        {tasks.length === 0 && (
                          <div style={{ padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                            No agents available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', width: '100%', maxWidth: '720px', marginTop: '22px' }}>
                {[
                  {
                    title: 'Analyse Performance',
                    desc: 'Review revenue, sessions and EPC',
                    q: 'Analyse performance: review revenue, sessions and EPC.',
                    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                      <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>
                    </svg>
                  },
                  {
                    title: 'Compare Periods',
                    desc: 'Compare current and previous performance',
                    q: 'Compare current period performance vs the previous period.',
                    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                      <path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"/><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"/><line x1="12" y1="8" x2="12" y2="16"/>
                    </svg>
                  },
                  {
                    title: 'Verify Anomalies',
                    desc: 'Investigate unexpected data changes',
                    q: 'Are there any anomalies or unexpected changes recently?',
                    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="11" y1="8" x2="11" y2="12"/><line x1="11" y1="14" x2="11.01" y2="14"/>
                    </svg>
                  }
                ].map((card, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestionClick(card.q)}
                    style={{
                      padding: '18px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      color: 'var(--text)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = 'var(--accent)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'rgba(139, 92, 246, 0.15)',
                      color: 'var(--accent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {card.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>{card.title}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{card.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  onFeedback={sendFeedback}
                  onCopy={copyToClipboard}
                />
              ))}
              
              {progress.visible && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  alignSelf: 'flex-start',
                  maxWidth: 'min(820px, 85%)',
                  width: '100%'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--surface2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <img src="/pearson_new.png" alt="Agent" style={{ width: '20px', height: '20px' }} />
                  </div>
                  <div style={{
                    background: progress.mode === 'thinking' ? 'var(--surface2)' : 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    flex: 1
                  }}>
                    {progress.mode === 'thinking' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--accent)', fontSize: '13px', fontWeight: 600 }}>
                          <span className="progress-pulse" style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'var(--accent)',
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }}></span>
                          Thinking
                        </div>
                        {progress.thinkingLines.map((line, i) => (
                          <div key={i} style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px', fontStyle: 'italic' }}>
                            {line}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>
                        <span className="progress-pulse" style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: 'var(--accent)',
                          animation: 'pulse 1.5s ease-in-out infinite'
                        }}></span>
                        {progress.text}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Composer pinned at bottom during an active conversation */}
        {(messages.length > 0 || progress.visible) && (
          <div style={{ padding: '14px 24px 18px' }}>
            <div style={{ maxWidth: '720px', margin: '0 auto' }}>
              {composer}
              {/* Agent selector below input on left side */}
              <div style={{
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'var(--text-muted)'
                }}>
                  Agent:
                </span>
                <div ref={datasetMenuRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowDatasetMenu((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={showDatasetMenu}
                    aria-label="Select agent"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      background: 'var(--surface2)',
                      border: `1px solid ${showDatasetMenu ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '11px',
                      fontWeight: 500,
                      transition: 'border-color 0.15s, color 0.15s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = 'var(--text)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = 'var(--text-muted)';
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '12px', height: '12px', flexShrink: 0 }}>
                      <rect x="5" y="8" width="14" height="10" rx="3"/>
                      <path d="M9 8V7a3 3 0 0 1 6 0v1"/>
                      <circle cx="9.5" cy="13" r="1" fill="currentColor" stroke="none"/>
                      <circle cx="14.5" cy="13" r="1" fill="currentColor" stroke="none"/>
                    </svg>
                    <span>
                      {selectedTask
                        ? (tasks.find((t) => t.task_name === selectedTask)?.display_name || selectedTask)
                        : 'Auto-select'}
                    </span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                      width: '10px',
                      height: '10px',
                      flexShrink: 0,
                      transform: showDatasetMenu ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s'
                    }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>

                  {showDatasetMenu && (
                    <div
                      role="listbox"
                      style={{
                        position: 'absolute',
                        left: 0,
                        bottom: 'calc(100% + 6px)',
                        minWidth: '280px',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                        zIndex: 3000,
                        padding: '4px'
                      }}
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={!selectedTask}
                        onClick={() => {
                          setSelectedTask(null);
                          setShowDatasetMenu(false);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          background: !selectedTask ? 'var(--surface2)' : 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'var(--text)',
                          fontSize: '13px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = !selectedTask ? 'var(--surface2)' : 'transparent';
                        }}
                      >
                        <span style={{ flex: 1, fontWeight: !selectedTask ? 600 : 400 }}>Auto-select</span>
                        {!selectedTask && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" style={{ width: '14px', height: '14px' }}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                      {tasks.map((task) => {
                        const active = selectedTask === task.task_name;
                        return (
                          <button
                            key={task.task_name}
                            type="button"
                            role="option"
                            aria-selected={active}
                            onClick={() => {
                              setSelectedTask(task.task_name);
                              setShowDatasetMenu(false);
                            }}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                              padding: '10px 12px',
                              background: active ? 'var(--surface2)' : 'transparent',
                              border: 'none',
                              borderRadius: '8px',
                              color: 'var(--text)',
                              fontSize: '13px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontFamily: 'inherit'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = active ? 'var(--surface2)' : 'transparent';
                            }}
                          >
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontWeight: active ? 600 : 500, lineHeight: 1.3 }}>
                                {task.display_name || task.task_name}
                              </span>
                              {task.bq_dataset && (
                                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                  {task.bq_dataset}
                                </span>
                              )}
                            </span>
                            {active && (
                              <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '2px' }}>
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            )}
                          </button>
                        );
                      })}
                      {tasks.length === 0 && (
                        <div style={{ padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          No agents available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      )}

      {/* Loading overlay - renders on top of everything when loading a session */}
      {isLoadingSession && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000
        }}>
          <div style={{
            background: 'var(--surface)',
            borderRadius: '12px',
            padding: '32px 48px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            border: '1px solid var(--border)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid var(--border)',
              borderTop: '4px solid var(--accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{
              fontSize: '15px',
              color: 'var(--text)',
              fontWeight: 500
            }}>
              Loading chat...
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setShowRenameModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', color: 'var(--text)', fontSize: '18px', fontWeight: 600 }}>
              Rename Chat
            </h3>
            <input
              type="text"
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameTitle.trim()) {
                  renameChat(showRenameModal, renameTitle.trim());
                  setShowRenameModal(null);
                } else if (e.key === 'Escape') {
                  setShowRenameModal(null);
                }
              }}
              placeholder="Enter new title"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                fontSize: '14px',
                fontFamily: 'inherit',
                outline: 'none',
                marginBottom: '16px'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setShowRenameModal(null)}
                style={{
                  padding: '8px 16px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--surface)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--surface2)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (renameTitle.trim()) {
                    renameChat(showRenameModal, renameTitle.trim());
                    setShowRenameModal(null);
                  }
                }}
                disabled={!renameTitle.trim()}
                style={{
                  padding: '8px 16px',
                  background: renameTitle.trim() ? 'var(--accent)' : 'var(--surface2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: renameTitle.trim() ? 'white' : 'var(--text-muted)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: renameTitle.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  opacity: renameTitle.trim() ? 1 : 0.5
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Project Modal */}
      {showAddToProjectModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowAddToProjectModal(null);
            setNewProjectName('');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--text)', fontSize: '18px', fontWeight: 600 }}>
              Add to Project
            </h3>

            {/* Create New Project Form */}
            <div style={{
              background: 'var(--surface2)',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
                Create New Project
              </div>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newProjectName.trim()) {
                    const projectId = await createProject(newProjectName.trim());
                    if (projectId && showAddToProjectModal) {
                      await addChatToProject(projectId, showAddToProjectModal);
                      await loadChatProjects(showAddToProjectModal);
                      setNewProjectName('');
                    }
                  }
                }}
                placeholder="Enter project name..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  marginBottom: '10px'
                }}
              />
              <button
                onClick={async () => {
                  if (newProjectName.trim()) {
                    const projectId = await createProject(newProjectName.trim());
                    if (projectId && showAddToProjectModal) {
                      await addChatToProject(projectId, showAddToProjectModal);
                      await loadChatProjects(showAddToProjectModal);
                      setNewProjectName('');
                    }
                  }
                }}
                disabled={!newProjectName.trim()}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: newProjectName.trim() ? 'var(--accent)' : 'var(--surface2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: newProjectName.trim() ? 'white' : 'var(--text-muted)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: newProjectName.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center',
                  transition: 'all 0.15s'
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create & Add
              </button>
            </div>

            {/* Existing Projects */}
            {projects.length > 0 && (
              <>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
                  Or Add to Existing
                </div>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  marginBottom: '16px',
                  minHeight: '100px'
                }}>
                  {projects.map(p => (
                    <label
                      key={p.project_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'var(--surface)';
                        e.currentTarget.style.borderColor = 'var(--accent)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'var(--surface2)';
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={chatProjects.has(p.project_id)}
                        onChange={async () => {
                        if (chatProjects.has(p.project_id)) {
                          await removeChatFromProject(p.project_id, showAddToProjectModal!);
                          setChatProjects(prev => {
                            const updated = new Set(prev);
                            updated.delete(p.project_id);
                            return updated;
                          });
                        } else {
                          await addChatToProject(p.project_id, showAddToProjectModal!);
                          setChatProjects(prev => new Set([...prev, p.project_id]));
                        }
                      }}
                      style={{ marginRight: '12px', cursor: 'pointer' }}
                    />
                    <span style={{ color: 'var(--text)', fontSize: '14px' }}>
                      {p.project_name}
                    </span>
                  </label>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddToProjectModal(null);
                  setNewProjectName('');
                }}
                style={{
                  padding: '8px 16px',
                  background: 'var(--accent)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowCreateProjectModal(false);
            setNewProjectName('');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--text)', fontSize: '18px', fontWeight: 600 }}>
              Create New Project
            </h3>
            
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newProjectName.trim()) {
                  await createProject(newProjectName.trim());
                  setShowCreateProjectModal(false);
                  setNewProjectName('');
                } else if (e.key === 'Escape') {
                  setShowCreateProjectModal(false);
                  setNewProjectName('');
                }
              }}
              placeholder="Enter project name..."
              autoFocus
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                fontSize: '14px',
                fontFamily: 'inherit',
                marginBottom: '20px'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateProjectModal(false);
                  setNewProjectName('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (newProjectName.trim()) {
                    await createProject(newProjectName.trim());
                    setShowCreateProjectModal(false);
                    setNewProjectName('');
                  }
                }}
                disabled={!newProjectName.trim()}
                style={{
                  padding: '10px 20px',
                  background: newProjectName.trim() ? 'var(--accent)' : 'var(--surface2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: newProjectName.trim() ? 'white' : 'var(--text-muted)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: newProjectName.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Project Modal */}
      {showRenameProjectModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowRenameProjectModal(null);
            setRenameProjectName('');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--text)', fontSize: '18px', fontWeight: 600 }}>
              Rename Project
            </h3>
            
            <input
              type="text"
              value={renameProjectName}
              onChange={(e) => setRenameProjectName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && renameProjectName.trim() && showRenameProjectModal) {
                  await renameProject(showRenameProjectModal, renameProjectName.trim());
                  setShowRenameProjectModal(null);
                  setRenameProjectName('');
                } else if (e.key === 'Escape') {
                  setShowRenameProjectModal(null);
                  setRenameProjectName('');
                }
              }}
              placeholder="Enter new project name..."
              autoFocus
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                fontSize: '14px',
                fontFamily: 'inherit',
                marginBottom: '20px'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRenameProjectModal(null);
                  setRenameProjectName('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (renameProjectName.trim() && showRenameProjectModal) {
                    await renameProject(showRenameProjectModal, renameProjectName.trim());
                    setShowRenameProjectModal(null);
                    setRenameProjectName('');
                  }
                }}
                disabled={!renameProjectName.trim()}
                style={{
                  padding: '10px 20px',
                  background: renameProjectName.trim() ? 'var(--accent)' : 'var(--surface2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: renameProjectName.trim() ? 'white' : 'var(--text-muted)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: renameProjectName.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s'
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Message bubble component
function MessageBubble({ message, onFeedback, onCopy }: {
  message: Message;
  onFeedback: (msgId: string, rating: 'up' | 'down', comment?: string) => Promise<boolean>;
  onCopy: (text: string) => void;
}) {
  const [showSql, setShowSql] = useState(false);
  const [commentMode, setCommentMode] = useState(false);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);

  const handleFeedback = async (rating: 'up' | 'down') => {
    setFeedbackError(false);
    if (rating === 'down') {
      // Prefill the previous comment so the user can edit it
      setComment(message.feedbackMeta?.comment || '');
      setCommentMode(true);
    } else if (message.feedbackMeta?.rating !== 'up') {
      setFeedbackError(!(await onFeedback(message.id, rating)));
    }
  };

  const submitComment = async () => {
    const ok = await onFeedback(message.id, 'down', comment.trim());
    setFeedbackError(!ok);
    if (ok) {
      // Keep the box open with the text on failure so nothing is lost
      setCommentMode(false);
      setComment('');
    }
  };

  const isUser = message.role === 'user';

  // Extract SQL from message content
  const sqlMatch = message.content.match(/```sql\s*\n([\s\S]*?)```/);
  const sqlCode = sqlMatch ? sqlMatch[1].trim() : null;
  const hasSql = !!sqlCode;

  // Extract chart from message content
  const chartMatch = message.content.match(/```chart\s*\n([\s\S]*?)```/);
  let chartData = null;
  if (chartMatch) {
    try {
      chartData = JSON.parse(chartMatch[1].trim());
    } catch (e) {
      console.error('Failed to parse chart data:', e);
    }
  }

  // Simple markdown renderer for agent responses (excludes SQL and chart blocks)
  const renderContent = (text: string) => {
    if (isUser) return text; // Don't parse user messages
    
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Remove ```sql and ```chart blocks from content (they'll be shown separately)
    html = html.replace(/```sql\s*\n[\s\S]*?```/g, '');
    html = html.replace(/```chart\s*\n[\s\S]*?```/g, '');

    // Handle markdown tables
    html = html.replace(/((?:\|[^\n]+\|\n?)+)/g, (block) => {
      const rows = block.trim().split(/\n/);
      if (rows.length < 2) return block;
      
      const headers = rows[0].split('|').map(h => h.trim()).filter(Boolean);
      const dataRows = rows.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
      
      let table = '<div style="overflow-x: auto; max-width: 100%; margin: 12px 0;"><table style="border-collapse: collapse; min-width: 100%; font-size: 13px;"><thead><tr>';
      headers.forEach(h => {
        table += `<th style="border: 1px solid var(--border); padding: 8px; text-align: left; background: var(--surface2); white-space: nowrap;">${h}</th>`;
      });
      table += '</tr></thead><tbody>';
      dataRows.forEach(row => {
        table += '<tr>';
        row.forEach(cell => {
          table += `<td style="border: 1px solid var(--border); padding: 8px; white-space: nowrap;">${cell}</td>`;
        });
        table += '</tr>';
      });
      table += '</tbody></table></div>';
      return table;
    });

    // Handle bold **text**
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Handle italic *text*
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Convert newlines to <br>
    html = html.replace(/\n/g, '<br>');

    return html;
  };

  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
      alignSelf: isUser ? 'flex-end' : 'flex-start',
      flexDirection: isUser ? 'row-reverse' : 'row',
      maxWidth: 'min(820px, 85%)',
      width: isUser ? 'auto' : '100%'
    }}>
      {/* Avatar */}
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: isUser ? 'var(--accent)' : 'var(--surface2)',
        color: isUser ? '#fff' : 'var(--text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: 0
      }}>
        {isUser ? 'You' : <img src="/pearson_new.png" alt="Agent" style={{ width: '20px', height: '20px' }} />}
      </div>

      {/* Content */}
      <div style={{ flex: isUser ? '0 1 auto' : 1, minWidth: 0, maxWidth: '100%' }}>
        <div style={{
          background: isUser ? 'var(--user-bg)' : message.role === 'error' ? '#ff000015' : 'var(--agent-bg)',
          border: `1px solid ${message.role === 'error' ? '#ff0000' : 'var(--border)'}`,
          borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
          padding: '12px 16px',
          color: message.role === 'error' ? '#ff0000' : 'var(--text)'
        }}>
          {isUser ? (
            <div style={{ fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {message.content}
            </div>
          ) : (
            <div 
              style={{ fontSize: '14px', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: renderContent(message.content) }}
            />
          )}
        </div>

        {/* Chart rendering */}
        {chartData && (
          <div style={{ marginTop: '12px' }}>
            <ChartRenderer data={chartData} />
          </div>
        )}

        {/* SQL collapsible section - shown when toggled */}
        {showSql && hasSql && (
          <pre style={{
            background: 'var(--surface2)',
            padding: '12px',
            borderRadius: '8px',
            overflow: 'auto',
            border: '1px solid var(--border)',
            marginTop: '8px',
            fontSize: '13px',
            lineHeight: '1.5',
            fontFamily: 'ui-monospace, monospace'
          }}>
            <code>{sqlCode}</code>
          </pre>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '6px',
          justifyContent: isUser ? 'flex-end' : 'flex-start'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>

          {message.role === 'agent' && (
            <>
              <button
                onClick={() => {
                  onCopy(message.content);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                style={{
                  background: copied ? 'var(--green)' : 'transparent',
                  border: 'none',
                  color: copied ? '#fff' : 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  fontSize: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.15s'
                }}
                title={copied ? 'Copied!' : 'Copy response'}
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                )}
              </button>

              {hasSql && (
                <button
                  onClick={() => setShowSql(!showSql)}
                  style={{
                    background: showSql ? 'var(--accent)' : 'var(--surface2)',
                    border: `1px solid ${showSql ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    color: showSql ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontFamily: 'ui-monospace, monospace',
                    fontWeight: 500,
                    transition: 'all 0.15s'
                  }}
                  title={showSql ? 'Hide SQL' : 'Show SQL'}
                >
                  &lt;sql&gt;
                </button>
              )}

              {message.feedbackMeta && (
                <>
                  <div style={{ width: '1px', height: '12px', background: 'var(--border)' }} />
                  <button
                    onClick={() => handleFeedback('up')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: message.feedbackMeta.rating === 'up' ? 'var(--green)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                    title="Good answer"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => handleFeedback('down')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: message.feedbackMeta.rating === 'down' ? 'var(--red)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                    title={message.feedbackMeta.comment ? `Bad answer: ${message.feedbackMeta.comment}` : 'Bad answer'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>
                    </svg>
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {commentMode && (
          <div style={{ marginTop: '8px' }}>
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  submitComment();
                } else if (e.key === 'Escape') {
                  setCommentMode(false);
                  setComment('');
                  setFeedbackError(false);
                }
              }}
              placeholder="What was wrong? (optional — press Enter)"
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                fontSize: '13px'
              }}
            />
          </div>
        )}

        {feedbackError && (
          <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--red)' }}>
            Couldn't save your feedback. Please try again.
          </div>
        )}

        {!commentMode && message.feedbackMeta?.rating === 'down' && message.feedbackMeta.comment && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
            Your feedback: <span style={{ color: 'var(--text)' }}>{message.feedbackMeta.comment}</span>
            <button
              onClick={() => handleFeedback('down')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: '12px',
                textDecoration: 'underline',
                marginLeft: '6px',
                padding: 0
              }}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
